/**
 * acgmeSpecialties.js — ACGME core specialty list for ChartMind-7.
 * Shaped by: CM7-02; decisions D-CM7-04, D-CM7-15, D-CM7-16, D-CM7-02-02, D-CM7-02-03, D-CM7-02-04.
 * Invariants: Owner adds specialties by setting `enabled: true` only after all 12 of that specialty's prompts exist.
 *   Do not edit SpecialtyKB, AAMC_SPECIALTIES, or any other specialty list from this file.
 *   acgmeCode is a string so leading zeros stay (D-CM7-02-03). Null means the code was not verified.
 * Related: chartMind7PromptRegistry.js, functions/src/chartMind7/comboPromptIds.js,
 *   firebase/seed/chartMind7.promptAddenda.json.
 */

/**
 * @typedef {Object} AcgmeSpecialty
 * @property {string} key
 * @property {string|null} acgmeCode
 * @property {string} displayName
 * @property {boolean} enabled
 * @property {string|null} specialtyKbSlug
 */

/** @type {AcgmeSpecialty[]} */
export const ACGME_SPECIALTIES = [
  {
    key: 'em',
    acgmeCode: '110',
    displayName: 'Emergency Medicine',
    enabled: true,
    specialtyKbSlug: 'emergency-medicine',
  },
  {
    key: 'im',
    acgmeCode: '140',
    displayName: 'Internal Medicine',
    enabled: true,
    specialtyKbSlug: 'internal-medicine',
  },
  {
    key: 'fm',
    acgmeCode: '120',
    displayName: 'Family Medicine',
    enabled: true,
    specialtyKbSlug: 'family-medicine',
  },
  {
    key: 'peds',
    acgmeCode: '320',
    displayName: 'Pediatrics',
    enabled: true,
    specialtyKbSlug: 'pediatrics',
  },
  {
    key: 'obgyn',
    acgmeCode: '220',
    displayName: 'Obstetrics and Gynecology',
    enabled: true,
    specialtyKbSlug: 'obstetrics-gynecology',
  },
  {
    key: 'surg',
    acgmeCode: '440',
    displayName: 'Surgery',
    enabled: true,
    specialtyKbSlug: 'general-surgery',
  },
  {
    key: 'allergy',
    acgmeCode: '020',
    displayName: 'Allergy and Immunology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'anesth',
    acgmeCode: '040',
    displayName: 'Anesthesiology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'colorectal',
    acgmeCode: '060',
    displayName: 'Colon and Rectal Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'derm',
    acgmeCode: '080',
    displayName: 'Dermatology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'ophtho',
    acgmeCode: '240',
    displayName: 'Ophthalmology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'ortho',
    acgmeCode: '260',
    displayName: 'Orthopaedic Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'ent',
    acgmeCode: '280',
    displayName: 'Otolaryngology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'path',
    acgmeCode: '300',
    displayName: 'Pathology',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'prevmed',
    acgmeCode: '380',
    displayName: 'Preventive Medicine',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'psych',
    acgmeCode: '400',
    displayName: 'Psychiatry',
    enabled: false,
    specialtyKbSlug: null,
  },
  {
    key: 'rad',
    acgmeCode: '420',
    displayName: 'Radiology – Diagnostic',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'neuro',
    acgmeCode: null,
    displayName: 'Neurology',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'neurosurg',
    acgmeCode: null,
    displayName: 'Neurological Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'nucmed',
    acgmeCode: null,
    displayName: 'Nuclear Medicine',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'pmr',
    acgmeCode: null,
    displayName: 'Physical Medicine and Rehabilitation',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'plastics',
    acgmeCode: null,
    displayName: 'Plastic Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'radonc',
    acgmeCode: null,
    displayName: 'Radiation Oncology',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'thoracic',
    acgmeCode: null,
    displayName: 'Thoracic Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'uro',
    acgmeCode: null,
    displayName: 'Urology',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'vascular',
    acgmeCode: null,
    displayName: 'Vascular Surgery',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'medgen',
    acgmeCode: null,
    displayName: 'Medical Genetics and Genomics',
    enabled: false,
    specialtyKbSlug: null,
  },
  // code not verified — check acgme.org before enabling
  {
    key: 'ir',
    acgmeCode: null,
    displayName: 'Interventional Radiology',
    enabled: false,
    specialtyKbSlug: null,
  },
];
